import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from './ProtectedRoute';
import ProtectedRouteByNotUser from './ProtectedRouteByNotUser';
import ScrollToTop from '../components/shared/ScrollToTop';
import Home from "../pages/home";
import Login from '../pages/login';
import Register from '../pages/register';
import NotFound from '../pages/NotFound';
import ModelsCatalog from '../pages/public/ModelsCatalog';
import ModelProfile from '../pages/public/ModelProfile';
import AdminLayout from '../layouts/AdminLayout';
import PanelLayout from '../layouts/PanelLayout';
import StreamingLayout from '../layouts/StreamingLayout';
import Admin from '../pages/admin/admin';
import Users from '../pages/admin/users';
import Gallery from '../pages/admin/gallery';
import MyProfile from '../pages/panel/MyProfile';
import MySupporters from '../pages/panel/MySupporters';
import List from '../pages/streaming/list';
import Live from '../pages/streaming/live';
import TokenPlans from '../pages/streaming/TokenPlans';
import LiveGuard from './LiveGuard';

/**
 * Mapa de rutas de la aplicación.
 *
 * Tres áreas privadas, una por tipo de trabajo, cada una con su layout como
 * ruta padre que pinta a sus hijos en el <Outlet />:
 *
 *   /admin  gestión del estudio (modelos, personas, galería)
 *   /panel  lo de la propia modelo (su perfil, sus apoyos)
 *   /live   consumo (salas y recarga)
 *
 * Cada ruta declara el permiso que la abre, nunca el rol: así mover una
 * capacidad de un rol a otro es cosa del servidor. Antes /admin significaba
 * dos cosas a la vez —el permiso `show_admin` lo tenía también la modelo— y el
 * layout la expulsaba nada más entrar.
 */
export default function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />

        {/* Vitrina pública: sin sesión ni pago */}
        <Route path="/modelos" element={<ModelsCatalog />} />
        <Route path="/modelo/:slug" element={<ModelProfile />} />

        <Route
          path="/login"
          element={<ProtectedRouteByNotUser><Login /></ProtectedRouteByNotUser>}
        />
        <Route
          path="/register"
          element={<ProtectedRouteByNotUser><Register /></ProtectedRouteByNotUser>}
        />

        {/* Gestión del estudio */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route
            index
            element={<ProtectedRoute permission="view_models"><Admin /></ProtectedRoute>}
          />
          <Route
            path="usuarios"
            element={
              <ProtectedRoute permission={['view_clients', 'view_all_users']}>
                <Users />
              </ProtectedRoute>
            }
          />
          {/* La pantalla se llamaba "clients" y solo enseñaba clientes. */}
          <Route path="clients" element={<Navigate to="/admin/usuarios" replace />} />
          <Route
            path="gallery"
            element={<ProtectedRoute permission="manage_gallery"><Gallery /></ProtectedRoute>}
          />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>

        {/* Panel de la modelo */}
        <Route path="/panel" element={<PanelLayout />}>
          <Route
            index
            element={<ProtectedRoute permission="edit_own_profile"><MyProfile /></ProtectedRoute>}
          />
          <Route
            path="apoyos"
            element={<ProtectedRoute permission="view_own_supporters"><MySupporters /></ProtectedRoute>}
          />
          <Route path="*" element={<Navigate to="/panel" replace />} />
        </Route>

        {/* Streaming: requiere sesión, y el vivo además recarga vigente */}
        <Route path="/live" element={<StreamingLayout />}>
          <Route
            index
            element={
              <ProtectedRoute permission="view_catalog">
                <List />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id"
            element={
              <ProtectedRoute permission="view_live">
                <LiveGuard><Live /></LiveGuard>
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="/recharge" element={<StreamingLayout />}>
          <Route
            index
            element={<ProtectedRoute permission="recharge_tokens"><TokenPlans /></ProtectedRoute>}
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
